import React, { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, database as realtimeDb } from "../FirebaseConfig";
import { ref, get } from "firebase/database";
import Lottie from "lottie-react";
import { FaChevronDown, FaHome,FaChevronUp,FaClipboardCheck,FaMapMarkerAlt, FaShippingFast, FaTruck } from "react-icons/fa"; // Import Icons

const API_URL = "https://us-central1-tghc-web.cloudfunctions.net/";

export default function OrderInfo() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [error, setError] = useState(null);
  const [trackingId, setTrackingId] = useState(null);
  const [animationData, setAnimationData] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [latestScan, setLatestScan] = useState(null);
  const [currentStageIndex, setCurrentStageIndex] = useState(-1);

  const [expandedProducts, setExpandedProducts] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const stages = [
    { label: "Order Created", icon: <FaClipboardCheck className="text-green-500 text-xl" />, status: "Manifested" },
    { label: "Pickup Scheduled", icon: <FaTruck className="text-yellow-500 text-xl" />, status: "Pickup scheduled" },
    { label: "Shipped", icon: <FaShippingFast className="text-blue-500 text-xl" />, status: "Shipment picked up" },
    { label: "In Transit", icon: <FaTruck className="text-orange-500 text-xl" />, status: "In Transit" },
    { label: "Out for Delivery", icon: <FaMapMarkerAlt className="text-red-500 text-xl" />, status: "Out for Delivery" },
  ];



  const steps = [
    { label: "Order Created", icon: <FaClipboardCheck /> },
    { label: "Pickup Scheduled", icon: <FaTruck /> },
    { label: "Order Shipped", icon: <FaShippingFast /> },
    { label: "In Transit", icon: <FaTruck /> },
    { label: "Out for Delivery", icon: <FaHome /> },
  ];
  

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch("/assets/General/orderLoad.json")
      .then((response) => response.json())
      .then((data) => setAnimationData(data))
      .catch((error) => console.error("Error loading animation:", error));
  }, []);

  useEffect(() => {
    const redirectPath =
      new URLSearchParams(location.search).get("redirect") || "/orders";

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const orderId = new URLSearchParams(location.search).get("id");
        if (orderId) {
          fetchOrderDetails(orderId);
        } else {
          setError("Invalid order ID.");
        }
      } else {
        setUser(null);
        navigate(`/login?redirect=${redirectPath}`);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const fetchOrderDetails = async (orderId) => {
    setLoading(true);
    try {
      const orderRef = ref(realtimeDb, `orders/${orderId}`);
      const snapshot = await get(orderRef);

      if (snapshot.exists()) {
        const orderData = snapshot.val();
        const currentUser = auth.currentUser;

    // if (currentUser && orderData.uId !== currentUser.uid) {
    //   navigate("/");
    //   return;
    // }

        setOrderDetails(orderData);
        setTrackingId(orderData.trackingId || null);
      } else {
        setError("Order not found.");
      }
    } catch (error) {
      console.error("Error fetching order:", error);
      setError("Failed to fetch order details.");
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (user && trackingId) {
      fetchTrackingInfo(user, trackingId);
    }
  }, [user, trackingId]);

  const fetchTrackingInfo = async (user, waybill) => {
    setTrackingLoading(true);
    try {
      const token = await user.getIdToken(true);
      const response = await fetch(`${API_URL}/trackOrder?waybill=${waybill}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
  
      const data = await response.json();
      if (data.ShipmentData?.length > 0) {
        const shipment = data.ShipmentData[0].Shipment;
        setTrackingData(shipment);
  
        // Ensure there's a valid scan
        if (shipment.Scans?.length > 0) {
          const lastScan = shipment.Scans[shipment.Scans.length - 1];
          setLatestScan(lastScan);
  
          // Determine current tracking stage
          // const stageIndex = stages.findIndex(stage => lastScan.ScanDetail.Scan.includes(stage.status));
          // setCurrentStageIndex(stageIndex !== -1 ? stageIndex : 0);

          const stageIndex = stages.findIndex(stage =>
            lastScan.ScanDetail.Scan.toLowerCase().includes(stage.status.toLowerCase()) ||
            lastScan.ScanDetail.Instructions.toLowerCase().includes(stage.status.toLowerCase())
          );

          setCurrentStageIndex(stageIndex !== -1 ? stageIndex : 0);


        }
      } else {
        setError("Tracking details not found.");
      }
    } catch (error) {
      console.error("Error fetching tracking info:", error);
      setError("Failed to fetch tracking information.");
    } finally {
      setTrackingLoading(false);
    }
  };
  

  if (loading) {
    return (
      <div className="h-screen flex justify-center items-center bg-gray-100">
        <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200 w-80 md:w-96 text-center">
          {/* Animation */}
          <div className="w-2/3 mx-auto">
            <Lottie animationData={animationData} loop autoPlay />
          </div>
  
          {/* Loading Text */}
          <p className="mt-4 text-sm md:text-base text-gray-700 font-medium">
            Fetching your order...
          </p>
  
          {/* Loading Dots Effect */}
          <div className="mt-2 flex justify-center space-x-2">
            <div className="w-2 h-2 bg-[#f47d02] rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-[#f47d02] rounded-full animate-bounce delay-150"></div>
            <div className="w-2 h-2 bg-[#f47d02] rounded-full animate-bounce delay-300"></div>
          </div>
        </div>
      </div>
    );
  }
  

  if (error) {
    return (
      <div className="h-screen flex justify-center items-center text-red-600">
        {error}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mt-27 mx-auto mt-10 p-6 space-y-6">
      {/* {trackingData && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4">Tracking Info</h2>
          <div className="border-l-4 border-blue-500 pl-4">
            <p className="text-gray-600 text-md">Tracking ID: <span className="font-bold">{trackingData.AWB}</span></p>
            <p className="text-gray-600">Current Status: 
              <span className="font-bold text-blue-600"> {trackingData.Status.Status}</span>
            </p>
            <p className="text-gray-600">Current Location: {trackingData.Status.StatusLocation}</p>
            <p className="text-gray-600">
              Expected Delivery: {trackingData.ExpectedDeliveryDate || "Not Available"}
            </p>

          </div>
          
          <div className="mt-6">
            <h3 className="text-md font-semibold text-gray-700">Tracking History:</h3>
            <div className="overflow-y-auto max-h-64 mt-2 p-2 bg-gray-100 rounded-lg">
              {trackingData.Scans?.slice().reverse().map((scan, index) => (
                <div key={index} className="flex space-x-3 p-3 bg-white rounded-lg shadow-md mb-2">
                  <FaMapMarkerAlt className="text-blue-500 mt-1" />
                  <div>
                    <p className="text-sm font-semibold">{scan.ScanDetail.Scan}</p>
                    <p className="text-sm text-gray-500">{new Date(scan.ScanDetail.ScanDateTime).toLocaleString()}</p>
                    <p className="text-sm text-gray-500">Location: {scan.ScanDetail.ScannedLocation}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )} */}

{trackingLoading ? (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Tracking Info</h2>
          <div className="animate-pulse">
            <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-300 rounded w-1/2"></div>
            <p className="mt-4 text-gray-500">Tracking your order...</p>
          </div>
        </div>
      ) : (
        trackingData && (
          // <div className="bg-white p-6 rounded-lg shadow-md">
          //   <h2 className="text-xl font-bold text-gray-800 mb-4">Tracking Info</h2>
          //   <p className="text-gray-600">
          //     Tracking ID: <span className="font-bold">{trackingData.AWB}</span>
          //   </p>
          //   <p className="text-gray-600">
          //     Current Status:{" "}
          //     <span className="font-bold text-blue-600">{trackingData.Status.Status}</span>
          //   </p>
          //   <p className="text-gray-600">Location: {trackingData.Status.StatusLocation}</p>

          //   <h3 className="mt-6 text-md font-semibold text-gray-700">Tracking History:</h3>
          //   <div className="overflow-y-auto max-h-64 mt-2 p-2 bg-gray-100 rounded-lg">
          //     {trackingData.Scans?.slice().reverse().map((scan, index) => (
          //       <div key={index} className="flex space-x-3 p-3 bg-white rounded-lg shadow-md mb-2">
          //         <FaMapMarkerAlt className="text-blue-500 mt-1" />
          //         <div>
          //           <p className="text-sm font-semibold">{scan.ScanDetail.Scan}</p>
          //           <p className="text-sm text-gray-500">
          //             {new Date(scan.ScanDetail.ScanDateTime).toLocaleString()}
          //           </p>
          //           <p className="text-sm text-gray-500">Location: {scan.ScanDetail.ScannedLocation}</p>
          //         </div>
          //       </div>
          //     ))}
          //   </div>
          // </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-bold text-gray-800 mb-4">Tracking Info</h2>
      <p className="text-gray-600">
        Tracking ID: <span className="font-bold">{trackingData.AWB}</span>
      </p>
      <p className="text-gray-600">
        Current Status: <span className="font-bold text-blue-600">{latestScan.ScanDetail.Instructions}</span>
      </p>
      <p className="text-gray-600">Location: {latestScan.ScanDetail.ScannedLocation}</p>
      <p className="text-gray-600 text-lg flex items-center space-x-2">
  <span className="font-semibold">📦 Expected Delivery:</span> 
  <span className={`font-bold ${trackingData.ExpectedDeliveryDate !== "N/A" ? "text-green-600" : "text-orange-500"}`}>
    {trackingData.ExpectedDeliveryDate !== "N/A" 
      ? new Date(trackingData.ExpectedDeliveryDate).toLocaleString() 
      : "Will be available soon 🚀"}
  </span>
</p>

      {/* const expectedDelivery = ; */}

      {/* Order Progress */}
      {/* <div className="mt-6">
        <h3 className="text-md font-semibold text-gray-700">Order Progress:</h3>
        <div className="flex space-x-6 items-center mt-4">
          {stages.map((stage, index) => (
            <div key={index} className={`flex flex-col items-center ${index <= currentStageIndex ? "opacity-100" : "opacity-50"}`}>
              {stage.icon}
              <p className="text-xs mt-1 font-semibold">{stage.label}</p>
              {index < stages.length - 1 && <div className="w-12 h-1 bg-gray-300 mt-2"></div>}
            </div>
          ))}
        </div>

        
      </div> */}

<div className="bg-white p-6 rounded-lg shadow-md">
      <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">
        Order Progress
      </h3>

    

     
      {/* Mobile View (Vertical Stepper) */}
      <div className="flex flex-wrap md:flex-nowrap md:space-x-6 mt-4">
  {steps.map((step, index) => (
    <div key={index} className="flex items-center space-x-3 mb-4 md:mb-0">
      <span
        className={`text-xl p-2 m-2 rounded-full ${
          index <= currentStageIndex
            ? "bg-green-600 text-white"
            : "bg-gray-300 text-gray-500"
        }`}
      >
        {step.icon}
      </span>
      <p
        className={`text-sm ${
          index <= currentStageIndex ? "text-green-600 font-bold" : "text-gray-500"
        }`}
      >
        {step.label}
      </p>
    </div>
  ))}
</div>


    </div>

      {/* View All Track Updates Button */}
      <button
        onClick={() => setShowPopup(true)}
        className="mt-6 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-md hover:bg-blue-700"
      >
        View All Track Updates
      </button>

      {/* Popup Modal for Tracking History */}
      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center" onClick={() => setShowPopup(false)}>
          <div className="bg-white md:w-1/2 w-80 p-5 rounded-lg shadow-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-800 mb-4">Tracking History</h3>
            <div className="overflow-y-auto max-h-64 p-2 bg-gray-100 rounded-lg">
              {trackingData.Scans.slice().reverse().map((scan, index) => (
                <div key={index} className="flex space-x-3 p-3 bg-white rounded-lg shadow-md mb-2">
                  <FaMapMarkerAlt className="text-blue-500 mt-1" />
                  <div>
                    <p className="text-sm font-semibold">{scan.ScanDetail.Instructions}</p>
                    <p className="text-sm text-gray-500">{new Date(scan.ScanDetail.ScanDateTime).toLocaleString()}</p>
                    <p className="text-sm text-gray-500">Location: {scan.ScanDetail.ScannedLocation}</p>
                  </div>
                </div>
              ))}
            </div>
            <button className="mt-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-md w-full" onClick={() => setShowPopup(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>

        )
      )}

      {orderDetails && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4">Order Details</h2>
          <div className="border-l-4 border-green-500 pl-4">
            <p className="text-gray-600 text-md">Order ID: <span className="font-bold">{new URLSearchParams(location.search).get("orderId")}</span></p>
            <p className="text-gray-600">Total Amount: <span className="font-bold text-green-600">₹{orderDetails.totalAmount}</span></p>
          </div>
          {orderDetails.products && orderDetails.products.length > 0 && (() => {
  const totalOriginalPrice = orderDetails.products.reduce((sum, product) => sum + (product.price * product.quantity), 0);
  const savings = totalOriginalPrice - orderDetails.totalAmount;

  return savings > 0 ? (
    <div className="mt-4 p-4 bg-green-100 border-l-4 border-green-500 rounded-lg shadow-md">
      <p className="text-sm md:text-lg lg:text-base font-semibold text-green-700">
        🎉 You Saved <span className="text-green-800 font-bold">₹{savings}</span> on this order!
      </p>
    </div>
  ) : null;
})()}

          <div className="mt-6">
            <h3 className="text-md font-semibold text-gray-700">Shipping Address:</h3>
            <p className="text-gray-600">{orderDetails.address.customerName}, {orderDetails.address.addressLine1}, {orderDetails.address.city}, {orderDetails.address.State} - {orderDetails.address.Pincode}</p>
          </div>

{/* Products Section with Dropdown */}
<div className="mt-6">
  <div
    className="flex justify-between items-center cursor-pointer bg-gray-100 p-3 rounded-lg"
    onClick={() => setExpandedProducts(!expandedProducts)}
  >
    <h3 className="text-md md:text-lg font-semibold text-gray-700">
      Products ({orderDetails.products.length})
    </h3>
    {expandedProducts ? (
      <FaChevronUp className="text-gray-600" />
    ) : (
      <FaChevronDown className="text-gray-600" />
    )}
  </div>

  {expandedProducts && (
     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
     {orderDetails.products.map((product, index) => {
       const discount = product.price - product.actualPrice; // Calculate discount
       return (
         <div
           key={index}
           className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg border shadow-sm"
         >
           <img
             src={product.thumbnail}
             alt={product.name}
             className="h-16 w-16 rounded-lg shadow-md"
           />
           <div>
             <p className="text-sm md:text-base font-medium">{product.name}</p>
             <p className="text-xs md:text-sm text-gray-500">
               Quantity: {product.quantity}
             </p>
             <div className="flex items-center space-x-2">
               <p className="text-xs md:text-sm text-gray-500">Price: ₹{product.actualPrice}</p>
             {discount>0 &&  <p className="text-xs md:text-sm text-gray-400 line-through">
                 ₹{product.price}
               </p>}
             </div>
             {discount > 0 && (
               <p className="text-xs md:text-sm text-green-600 font-semibold">
                 You save ₹{discount*product.quantity}
               </p>
             )}
           </div>
         </div>
        );
      })}
    </div>
  )}
</div>

        </div>
      )}
    </div>
  );
}
